import { z } from "zod";
import { accessExecutionGrantV1Schema, accessPolicyModeSchema } from "./access-control.js";
import { runModelConfigSchema } from "./model-config.js";
import { taskRunCapabilitySchema } from "./primitives.js";
import { researchWarehouseProfileSchema } from "./research-warehouse.js";
import { PROJECT_VENUS_MODEL, isProjectVenusModel, isVenusQwen38VerdaModel, VENUS_NOVA_MODEL, VENUS_QWEN38_QUASAR_MODAL_MODEL } from "./self-hosted-experiment.js";
export function isNativeVenusModelConfig(config) {
    // Lightning owns its compact action stream in the Electric entity. Never let
    // the selected serving provider redirect it into the native Venus protocol.
    if (config?.harness === "venus-lightning-v1" || config?.harness === "venus-lightning-v2")
        return false;
    const executor = config?.executor;
    if (executor?.evaluationHarness) {
        const supported = executor.provider === "modal" && (executor.model === VENUS_NOVA_MODEL || executor.model === PROJECT_VENUS_MODEL) ||
            executor.provider === "openai" && (executor.model === "gpt-5.6-sol" || executor.model === "gpt-6-astra");
        return supported && executor.evaluationHarness === "venus-native";
    }
    return executor?.provider === "verda" && (isProjectVenusModel(executor.model) || isVenusQwen38VerdaModel(executor.model)) ||
        executor?.provider === "modal" && (isProjectVenusModel(executor.model) || executor.model === VENUS_NOVA_MODEL || executor.model === VENUS_QWEN38_QUASAR_MODAL_MODEL);
}
export const VENUS_NATIVE_MAX_TOOL_CALLS = 300;
export const VENUS_NATIVE_CONTINUATION_RESERVE_TOKENS = 256;
export const VENUS_NATIVE_TURN_DEADLINE_MS = 600_000;
export function nativeVenusDeadlineAt(startedAt) {
    if (!Number.isSafeInteger(startedAt) || startedAt <= 0)
        throw new Error("venus_native_start_invalid");
    return startedAt + VENUS_NATIVE_TURN_DEADLINE_MS;
}
export const venusNativeHistoryRowSchema = z.object({
    key: z.string(), runId: z.string(), attemptNumber: z.number().int().positive(),
    ordinal: z.number().int().nonnegative(), message: z.unknown(), createdAt: z.number(),
});
/** Assistant tool intents are reservations, including interrupted calls whose outcome is unknown. */
export function nativeVenusReservedToolCalls(rows, runId) {
    const keys = new Set();
    const calls = new Set();
    for (const row of rows.filter(row => row.runId === runId)) {
        if (keys.has(row.key))
            throw new Error("venus_native_duplicate_history_key");
        keys.add(row.key);
        const message = row.message;
        if (message?.role !== "assistant")
            continue;
        if (!Array.isArray(message.content))
            throw new Error("venus_native_history_content_invalid");
        for (const part of message.content) {
            if (part.type !== "toolCall")
                continue;
            if (!part.id || calls.has(part.id))
                throw new Error("venus_native_duplicate_call_id");
            calls.add(part.id);
        }
    }
    if (calls.size > VENUS_NATIVE_MAX_TOOL_CALLS)
        throw new Error("venus_native_tool_budget_exceeded");
    return calls.size;
}
/** Internal, worker-authenticated dispatch. Clients never supply execution authority. */
export const venusNativeTurnSchema = z.object({
    runId: z.string().min(1), sessionId: z.string().min(1),
    workerId: z.string().min(1), attemptNumber: z.number().int().positive(),
    deadlineAt: z.number().int().positive(),
    tenantSlug: z.string().min(1), userId: z.string().min(1),
    verifiedSubjectId: z.string().optional(), pluginSubjectId: z.string().optional(),
    organisationId: z.string().optional(), allowedOrganisationIds: z.array(z.string().min(1)),
    warehouseProfile: researchWarehouseProfileSchema.optional(),
    capabilities: z.array(taskRunCapabilitySchema),
    accessPolicyMode: accessPolicyModeSchema, executionGrants: z.array(accessExecutionGrantV1Schema),
    attachedChannelId: z.string().optional(), attachedChannelAdminAccess: z.boolean(),
    prompt: z.string().min(1),
    modelConfig: runModelConfigSchema.refine(isNativeVenusModelConfig, "Native Venus requires an explicit Venus model"),
    history: z.array(venusNativeHistoryRowSchema),
}).strict();
export const VENUS_NATIVE_LANE_PREFIX = "venus-native:";
